import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Paper,
  Card,
  CardContent,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  DirectionsCar as CarIcon,
  Schedule as ScheduleIcon,
  CreditCard as CardIcon,
  Engineering as EngineeringIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import api from '../services/api';

const CompletedJobModal = ({ open, onClose, bookingId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [jobData, setJobData] = useState(null);

  useEffect(() => {
    if (bookingId && open) {
      fetchJobDetails();
    }
  }, [bookingId, open]);

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/v1/bookings/${bookingId}/completed-details/`);
      setJobData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setJobData(null);
    setError('');
    onClose();
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <CheckCircleIcon sx={{ mr: 1, color: 'success.main' }} />
            <Typography variant="h6">Completed Job Details</Typography>
          </Box>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && jobData && (
          <>
            <Grid container spacing={3}>
              {/* Customer & Vehicle Information */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Customer & Vehicle
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      <strong>Customer:</strong> {jobData.booking.customer.full_name}
                    </Typography>
                  </Box>

                  {jobData.booking.customer.phone && (
                    <Box display="flex" alignItems="center" mb={1} ml={4}>
                      <Typography variant="body2" color="text.secondary">
                        Phone: {jobData.booking.customer.phone}
                      </Typography>
                    </Box>
                  )}

                  {jobData.booking.customer.email && (
                    <Box display="flex" alignItems="center" mb={1} ml={4}>
                      <Typography variant="body2" color="text.secondary">
                        Email: {jobData.booking.customer.email}
                      </Typography>
                    </Box>
                  )}

                  <Box display="flex" alignItems="center" mb={1} mt={2}>
                    <CarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      <strong>Vehicle:</strong> {jobData.booking.vehicle.make} {jobData.booking.vehicle.model}
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="center" mb={1} ml={4}>
                    <Typography variant="body2" color="text.secondary">
                      Registration: {jobData.booking.vehicle.registration}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              {/* Booking Information */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Booking Information
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <ScheduleIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      <strong>Booking Date:</strong> {new Date(jobData.booking.booking_date).toLocaleDateString()}
                    </Typography>
                  </Box>

                  {jobData.booking.time_of_day && (
                    <Box display="flex" alignItems="center" mb={1} ml={4}>
                      <Typography variant="body2" color="text.secondary">
                        Time: {jobData.booking.time_of_day}
                      </Typography>
                    </Box>
                  )}

                  <Box display="flex" alignItems="center" mb={1}>
                    <EngineeringIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      <strong>Technician:</strong> {jobData.booking.assigned_technician?.full_name || 'N/A'}
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="center" mb={1}>
                    <CheckCircleIcon sx={{ mr: 1, color: 'success.main' }} />
                    <Typography variant="body2">
                      <strong>Completed:</strong> {new Date(jobData.booking.actual_completion_time).toLocaleString()}
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="center" mb={1}>
                    <CardIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      <strong>Total Cost:</strong> £{jobData.booking.total_cost}
                    </Typography>
                  </Box>

                  {/* Completion Reasons */}
                  {jobData.booking.completion_reasons && jobData.booking.completion_reasons.length > 0 && (
                    <Box mt={2}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Completion Reasons:</strong>
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {jobData.booking.completion_reasons.map((reason) => {
                          const reasonConfig = {
                            no_show: { label: "Customer didn't turn up", color: 'error' },
                            vhc_ok: { label: 'VHC is all OK', color: 'success' },
                            customer_declined: { label: 'Customer declined work', color: 'warning' },
                            warranty_goodwill: { label: 'Warranty/Goodwill job', color: 'info' },
                            other: { label: 'Other', color: 'default' },
                          };
                          const config = reasonConfig[reason] || { label: reason, color: 'default' };
                          return (
                            <Chip
                              key={reason}
                              label={config.label}
                              size="small"
                              color={config.color}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                  {/* Completion Notes */}
                  {jobData.booking.completion_notes && (
                    <Box mt={2}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Completion Notes:</strong>
                      </Typography>
                      <Box
                        sx={{
                          p: 1.5,
                          backgroundColor: 'grey.50',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'grey.200',
                        }}
                      >
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                          {jobData.booking.completion_notes}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>

            {/* Work Required */}
            {jobData.booking.work_required && jobData.booking.work_required.length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom>
                  Work Required
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {jobData.booking.work_required.map((work, index) => (
                    <Chip
                      key={index}
                      label={`${work.service} - £${work.cost}`}
                      variant="outlined"
                    />
                  ))}
                </Box>
              </>
            )}

            {/* Vehicle Health Check */}
            {jobData.health_check && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom>
                  <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Vehicle Health Check
                </Typography>

                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="subtitle2">
                        Document: {jobData.health_check.document_number}
                      </Typography>
                      <Chip
                        label={jobData.health_check.status}
                        color="success"
                        size="small"
                      />
                    </Box>

                    {jobData.health_check.general_notes && (
                      <Box mb={2}>
                        <Typography variant="body2" fontWeight="bold" gutterBottom>
                          General Notes:
                        </Typography>
                        <Typography variant="body2" sx={{
                          p: 1.5,
                          backgroundColor: 'grey.50',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'grey.200',
                          whiteSpace: 'pre-line'
                        }}>
                          {jobData.health_check.general_notes}
                        </Typography>
                      </Box>
                    )}

                    {/* Inspection Items Summary */}
                    {jobData.health_check.inspection_items && jobData.health_check.inspection_items.length > 0 && (
                      <Box mb={2}>
                        <Typography variant="body2" fontWeight="bold" gutterBottom>
                          Inspection Summary:
                        </Typography>
                        <Grid container spacing={1}>
                          {jobData.health_check.inspection_items.map((item, index) => {
                            // Map status to colors
                            const statusConfig = {
                              'green': { color: 'success', label: 'Green - Satisfactory' },
                              'amber': { color: 'warning', label: 'Amber - Attention Soon' },
                              'red': { color: 'error', label: 'Red - Immediate Attention' },
                              'not_checked': { color: 'default', label: 'Not Checked' }
                            };
                            const config = statusConfig[item.status] || { color: 'default', label: item.status };

                            return (
                              <Grid item xs={12} md={6} key={index}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Chip
                                    label={config.label}
                                    color={config.color}
                                    size="small"
                                    sx={{ minWidth: 120 }}
                                  />
                                  <Typography variant="caption" sx={{ fontWeight: 'medium' }}>
                                    {item.item_name}
                                  </Typography>
                                  {item.recommendations && (
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                      - {item.recommendations}
                                    </Typography>
                                  )}
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    )}

                    {/* Videos */}
                    {jobData.health_check.videos && jobData.health_check.videos.length > 0 && (
                      <Box>
                        <Typography variant="body2" fontWeight="bold" gutterBottom>
                          Inspection Videos:
                        </Typography>
                        <Box display="flex" gap={2} flexWrap="wrap">
                          {jobData.health_check.videos.map(video => {
                            const videoUrl = video.file_url.startsWith('http')
                              ? video.file_url
                              : `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${video.file_url}`.replace('/api', '');

                            return (
                              <Box
                                key={video.id}
                                sx={{
                                  width: 300,
                                  border: '1px solid #ddd',
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  backgroundColor: '#000'
                                }}
                              >
                                <video
                                  src={videoUrl}
                                  style={{ width: '100%', display: 'block' }}
                                  controls
                                />
                                {video.caption && (
                                  <Typography variant="caption" sx={{ p: 1, display: 'block', backgroundColor: '#fff' }}>
                                    {video.caption}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompletedJobModal;
